import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Dropdown, Modal } from 'react-bootstrap-5';

import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV, faPencilAlt } from '@fortawesome/pro-solid-svg-icons';

import { Button } from 'components/ui/button';
import { DATE_FORMAT, timeFormat } from 'helpers/formatters/date_time_formats';
import { isMobile } from 'helpers/mobile_helpers';
import { isPresent } from 'helpers/string_helpers';

import DeleteTaskModal from 'components/modals/delete_task_modal';
import RemoveFromListTaskModal from 'components/modals/remove_from_list_task_modal';
import UnstyledDropdownToggle from 'components/bootstrap_extensions/bs5/unstyled_dropdown_toggle';

import LabeledProfilePhoto from './labeled_profile_photo';
import MobileTaskForm from './mobile_task_form';
import TaskAssociations from './task_associations';
import TaskCheckbox from './task_checkbox';
import TaskForm from './task_form';
import TaskIcon from './task_icon';
import VoucherApprovalTaskRow from './voucher_approval_task_row';
import CallListTaskRow from './call_list_task_row';

import './styles/task_list.scss';

const timestampFormats = {
  sameDay: `[Today at] ${timeFormat()}`,
  nextDay: '[Tomorrow]',
  nextWeek: DATE_FORMAT,
  lastDay: '[Yesterday]',
  lastWeek: '[Last] dddd',
  sameElse: DATE_FORMAT
};

export default class TaskList extends React.Component {
  static propTypes = {
    availableDatabases: PropTypes.arrayOf(PropTypes.array).isRequired,
    callList: PropTypes.object,
    callListsAccess: PropTypes.bool,
    databaseMembers: PropTypes.array.isRequired,
    defaultAssociatedModels: PropTypes.array,
    hideActions: PropTypes.bool,
    hideAssociations: PropTypes.bool,
    hideByProjectSelect: PropTypes.bool,
    hideCallListSelect: PropTypes.bool,
    hideContactSelect: PropTypes.bool,
    hideItemActions: PropTypes.bool,
    hideProjectSelect: PropTypes.bool,
    hidePropertySelect: PropTypes.bool,
    isFromCallList: PropTypes.bool,
    multipleProjectsInTask: PropTypes.bool,
    newTaskFormStyling: PropTypes.bool,
    projectId: PropTypes.number,
    selectedId: PropTypes.number,
    showLoadMoreLink: PropTypes.bool,
    tasks: PropTypes.arrayOf(PropTypes.shape({
      assignee: PropTypes.string.isRequired,
      assigneeImageUrl: PropTypes.string,
      assigneeInitials: PropTypes.string,
      associatedModels: PropTypes.array.isRequired,
      body: PropTypes.string,
      completed: PropTypes.bool,
      dueDate: PropTypes.string,
      id: PropTypes.number.isRequired,
      calendarImport: PropTypes.bool,
      owner: PropTypes.string.isRequired,
      ownerImageUrl: PropTypes.string,
      ownerInitials: PropTypes.string,
      taskType: PropTypes.string.isRequired,
      title: PropTypes.string,
      url: PropTypes.string
    })).isRequired,
    title: PropTypes.string,
    onDeleteTask: PropTypes.func.isRequired,
    onLoadMoreTasks: PropTypes.func,
    onSaveTask: PropTypes.func,
    onToggleTaskState: PropTypes.func.isRequired,
    onUpdateTask: PropTypes.func.isRequired
  };

  state = {
    duplicateTask: null,
    editingTask: null,
    deletingTask: null,
    removeFromListTask: null,
    taskFormErrors: null
  }

  // TODO: change callListsAccess to false once all other areas using this component pass it as props
  static defaultProps = {
    callListsAccess: true,
    hideActions: false,
    hideAssociations: false,
    hideCallListSelect: true,
    hideItemActions: false,
    showLoadMoreLink: false,
    newTaskFormStyling: false
  };

  static columnCount = 7;

  handleLoadMoreClick = (e) => {
    const { onLoadMoreTasks } = this.props;
    e.preventDefault();
    if (onLoadMoreTasks != null) {
      onLoadMoreTasks();
    }
  }

  handleTaskFormClose = () => {
    this.setState({
      editingTask: null,
      deletingTask: null,
      duplicateTask: null,
      removeFromListTask: null,
      taskFormErrors: null
    });
  }

  handleDeleteTask = () => {
    this.props.onDeleteTask(this.state.deletingTask);
    this.handleTaskFormClose();
  }

  showDeleteTaskModal = (task) => {
    this.setState({
      deletingTask: task
    });
  }

  showDuplicateTaskModal = (task) => {
    const { ...duplicateTask } = { ...task };
    delete duplicateTask.id;

    duplicateTask.taskListItems.forEach((item) => {
      delete item.task_id;
      delete item.id;
    });

    this.setState({
      duplicateTask: duplicateTask
    });
  }

  showRemoveTaskModal = (task) => {
    this.setState({
      removeFromListTask: task
    });
  }

  handleRemoveTaskFromList = () => {
    const { callList } = this.props;
    const { removeFromListTask } = this.state;

    const index = removeFromListTask.taskListItems.findIndex(item => item.taskListId === callList.id);
    removeFromListTask.taskListItems[index]["_destroy"] = true;
    this.handleTaskUpdate(removeFromListTask);
  }

  handleTaskEdit = (task, mobile = false) => {
    if (!mobile) {
      this.setState({
        editingTask: task
      });
    }
  }

  handleTaskSelect = (task) => {
    const { hideItemActions } = this.props;
    if (hideItemActions) {
      this.setState({
        editingTask: task
      });
    }
  }

  handleTaskDelete = (task) => {
    const { onDeleteTask } = this.props;
    onDeleteTask(task);
    this.setState({
      editingTask: null
    });
  }

  handleTaskUpdate = (task) => {
    const { onUpdateTask } = this.props;
    onUpdateTask(
      task,
      this.handleTaskFormClose,
      errors => this.setState({ taskFormErrors: errors })
    );
  }

  handleTaskSave = (task) => {
    const { onSaveTask } = this.props;

    task.associatedModels.forEach((model) => {
      delete model.id;
    });

    onSaveTask(
      task,
      this.handleTaskFormClose,
      errors => this.setState({ taskFormErrors: errors })
    );
  }

  render() {
    return (
      <React.Fragment>
        {this.renderTaskRows()}
        {this.renderLoadMoreLink()}
        {isMobile() ? this.renderMobileEditTaskForm() : this.renderEditTaskModal()}
        {this.renderDeleteTaskModal()}
        {this.props.isFromCallList && this.renderDuplicateTaskModal()}
        {this.props.isFromCallList && this.renderRemoveListTaskModal()}
      </React.Fragment>
    );
  }

  renderTaskRows() {
    const { tasks } = this.props;

    if (tasks.length > 0) {
      return (
        <React.Fragment>
          {this.renderTasks()}
        </React.Fragment>
      );
    } else {
      return (
        <tr>
          <td className="text-muted" colSpan="100%">
            No tasks found.
          </td>
        </tr>
      );
    }
  }

  renderTasks() {
    const {
      hideActions,
      hideAssociations,
      hideItemActions,
      isFromCallList,
      tasks,
      onToggleTaskState
    } = this.props;

    return tasks.map((task) => {
      if (task.taskType === 'voucher_approval') return <VoucherApprovalTaskRow task={task} />;
      if (isFromCallList) return (
        <CallListTaskRow
          completed={task.completed}
          isMobile={isMobile()}
          key={task.id}
          renderTaskDropdown={this.renderTaskDropdown}
          task={task}
          onHandleTaskEdit={this.handleTaskEdit}
          onToggleTaskState={onToggleTaskState}
        />
      );

      const rowClassNames = classNames({ task: true, 'task-completed': task.completed });

      return (
        <tr className={rowClassNames} key={task.id} name="task-list-item" onClick={() => this.handleTaskSelect(task)}>
          <td className="task-checkbox align-middle">
            <TaskCheckbox size="20px" task={task} onTaskStateChange={onToggleTaskState} />
          </td>

          <td className="clickable align-middle" onClick={() => this.handleTaskEdit(task, isMobile())}>
            <div className="task-text">{task.title || task.body}</div>
          </td>

          {!hideAssociations && (
            <td className="d-none d-md-table-cell align-middle">
              <TaskAssociations associations={task.associatedModels} id={task.id} />
            </td>
          )}

          <td
            className="clickable task-icon d-none d-md-table-cell align-middle"
            onClick={() => this.handleTaskEdit(task, isMobile())}
          >
            <TaskIcon size="sm" taskIsComplete={task.completed} type={task.taskType} />
          </td>

          <td className="clickable d-none d-lg-table-cell align-middle" onClick={() => this.handleTaskEdit(task, isMobile())}>
            <LabeledProfilePhoto
              initials={task.ownerInitials}
              label="Creator"
              photoUrl={task.ownerImageUrl}
              value={task.owner}
            />
          </td>

          <td className="clickable d-none d-md-table-cell align-middle" onClick={() => this.handleTaskEdit(task, isMobile())}>
            <LabeledProfilePhoto
              initials={task.assigneeInitials}
              label="Assignee"
              photoUrl={task.assigneeImageUrl}
              value={task.assignee}
            />
          </td>

          <td className="clickable d-none d-md-table-cell align-middle" onClick={() => this.handleTaskEdit(task, isMobile())}>
            {this.renderDueDate(task)}
          </td>

          {!hideActions && <td className="clickable d-none d-md-table-cell align-middle">{this.renderTaskDropdown(task)}</td>}
          {!hideItemActions && (
            <td className="d-table-cell d-md-none clickable align-middle">
              <Button
                className="edit-icon float-end"
                icon={faPencilAlt}
                name="edit-task-btn"
                size="sm"
                onClick={() => this.handleTaskEdit(task)}
              />
            </td>
          )}
        </tr>
      );
    });
  }

  renderTaskDropdown = task => (
    <Dropdown
      className="list-item-actions-dropdown position-static"
      id={`list-item-actions-dropdown-${task.id}`}
      pullRight
    >
      <Dropdown.Toggle as={UnstyledDropdownToggle}>
        <FontAwesomeIcon
          className='text-muted fa-sm clickable me-4'
          icon={faEllipsisV}
          style={{ fontSize: '20px' }}
        />
      </Dropdown.Toggle>
      <Dropdown.Menu className="position-absolute">
        {this.props.isFromCallList &&
          <Dropdown.Item key='duplicate-task-list-item' onClick={() => this.showDuplicateTaskModal(task)}>
            Duplicate Task
          </Dropdown.Item>
        }
        {(task.taskListItems && task.taskListItems.length >= 2 && this.props.isFromCallList) &&
          <Dropdown.Item key='remove-task-list-item' onClick={() => this.showRemoveTaskModal(task)}>
          Remove Task from List
          </Dropdown.Item>
        }
        <Dropdown.Item key='delete-task-list-item' onClick={() => this.showDeleteTaskModal(task)}>
          Delete Task
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  )

  renderLabeledValue(label, content, completed) {
    return (
      <>
        <span className="text-muted text-uppercase me-2">{label}:</span>
        {!completed && content}
      </>
    );
  }

  renderDueDate(task) {
    if (task.completed) {
      const completedAtText = moment(task.completedAt).calendar(null, timestampFormats);

      return this.renderLabeledValue("Completed", <span>{completedAtText}</span>);
    }

    if (isPresent(task.dueDate)) {
      const dueDate = moment(task.dueDate);
      const dueDateText = dueDate.calendar(null, timestampFormats);
      let display;
      if (!task.completed && dueDate.isBefore(moment())) {
        // Past due
        display = <strong className="text-danger">{dueDateText}</strong>;
      } else if (!task.completed && dueDate.isBefore(moment().add(2, 'days'))) {
        // Almost due
        display = <strong>{dueDateText}</strong>;
      } else {
        display = <span>{dueDateText}</span>;
      }

      return this.renderLabeledValue("Due", display);
    }
  }

  renderLoadMoreLink() {
    const { showLoadMoreLink } = this.props;

    if (showLoadMoreLink) {
      return (
        <tr>
          <td colSpan="100%">
            <a href="#" name='load-more-results-link' onClick={this.handleLoadMoreClick}>Show more...</a>
          </td>
        </tr>
      );
    }
  }

  renderTaskForm(task, onSave, onDelete) {
    const {
      availableDatabases,
      databaseMembers,
      hideByProjectSelect,
      hideCallListSelect,
      hideContactSelect,
      hideProjectSelect,
      hidePropertySelect,
      selectedId,
      isFromCallList
    } = this.props;

    return (
      <TaskForm
        assigneeOptions={databaseMembers}
        availableDatabaseOptions={availableDatabases}
        defaultAssociatedModels={this.props.defaultAssociatedModels || []}
        defaultTaskAssignee={databaseMembers.find(member => member.id === selectedId) || databaseMembers[0]}
        errors={this.state.taskFormErrors}
        existingTask={task}
        hideByProjectSelect={hideByProjectSelect}
        hideCallListSelect={hideCallListSelect}
        hideContactSelect={hideContactSelect}
        hideProjectSelect={hideProjectSelect}
        hidePropertySelect={hidePropertySelect}
        isFromCallList={isFromCallList}
        projectId={this.props.projectId}
        onCancel={this.handleTaskFormClose}
        onDelete={onDelete}
        onSave={onSave}
      />
    );
  }

  renderEditTaskModal() {
    const { editingTask } = this.state;
    if (!editingTask) return;

    return (
      <Modal
        backdrop="static"
        centered
        className="contact-modal overflow-y-auto"
        name="task-form-modal"
        show
        size="lg"
        onHide={this.handleTaskFormClose}
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit A Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {this.renderTaskForm(editingTask, this.handleTaskUpdate, this.handleTaskDelete)}
        </Modal.Body>
      </Modal>
    );
  }

  renderMobileEditTaskForm() {
    const {
      availableDatabases,
      callListsAccess,
      databaseMembers,
      hideByProjectSelect,
      hideCallListSelect,
      hideContactSelect,
      hideProjectSelect,
      hidePropertySelect,
      isFromCallList,
      newTaskFormStyling,
      multipleProjectsInTask,
      selectedId
    } = this.props;
    const { editingTask } = this.state;

    const assigneeOptions = databaseMembers;

    if (editingTask != null) {
      return (
        <div className="d-block d-md-none tasks-index-mobile-content tasks-mobile-content pt-0 pb-0 ps-0 pe-0">
          <MobileTaskForm
            assigneeOptions={assigneeOptions}
            availableDatabaseOptions={availableDatabases}
            callListsAccess={callListsAccess}
            defaultAssociatedModels={this.props.defaultAssociatedModels || []}
            defaultTaskAssignee={databaseMembers.find(member => member.id === selectedId) || databaseMembers[0]}
            errors={this.state.taskFormErrors}
            existingTask={editingTask}
            hideByProjectSelect={hideByProjectSelect}
            hideCallListSelect={hideCallListSelect}
            hideContactSelect={hideContactSelect}
            hideProjectSelect={hideProjectSelect}
            hidePropertySelect={hidePropertySelect}
            isFromCallList={isFromCallList}
            multipleProjects={multipleProjectsInTask}
            newTaskFormStyling={newTaskFormStyling}
            onCancel={this.handleTaskFormClose}
            onDelete={this.handleTaskDelete}
            onSave={this.handleTaskUpdate}
          />
        </div>
      );
    }
  }


  renderDuplicateTaskModal() {
    const { duplicateTask } = this.state;

    if (duplicateTask != null) {
      return (
        <Modal
          className='contact-modal overflow-y-auto'
          name='task-form-modal'
          show={duplicateTask != null}
          onHide={this.handleTaskFormClose}
        >
          <Modal.Header closeButton>
            <Modal.Title>Duplicate A Task</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {this.renderTaskForm(duplicateTask, this.handleTaskSave, null)}
          </Modal.Body>
        </Modal>
      );
    }
  }

  renderDeleteTaskModal() {
    const { deletingTask } = this.state;
    return (
      <DeleteTaskModal
        shown={deletingTask != null}
        onCancel={this.handleTaskFormClose}
        onDelete={this.handleDeleteTask}
      />
    );
  }

  renderRemoveListTaskModal() {
    const { callList } = this.props;
    const { removeFromListTask } = this.state;
    return (
      <RemoveFromListTaskModal
        callList={callList}
        shown={removeFromListTask != null}
        onCancel={this.handleTaskFormClose}
        onDelete={this.handleRemoveTaskFromList}
      />
    );
  }
}
